import { isRootProject } from '@/components/organization/organizationUtils'
import { useActionTypesStore } from '@/stores/actionTypesStore'
import { type ConsoleUser, type PlatformUser, isPlatformUser, isUserAdmin, isUserOwner } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { hasAnyExecutableActionType } from '@/utils/access-control/perEntity'
import { useMemo } from 'react'

export { isUserOwner }

/** Check number of organizations created by the user. */
export function getCreatedOrganizationsCount(user: ConsoleUser | PlatformUser): number {
	if (!user.organizations) {
		return 0
	}
	return user.organizations.filter(org => org.created_by === user.email).length
}

/** Org-scoped ownership check for guarding owner-only org APIs (metrics, org users, etc.). */
export function isOwnerOfOrganization(
	user: ConsoleUser | PlatformUser | null | undefined,
	organizationId: string,
): boolean {
	if (!user?.email || !organizationId) {
		return false
	}
	const email = user.email
	const orgFromList = user.organizations?.find(o => o.id === organizationId)
	if (orgFromList) {
		return orgFromList.owners?.includes(email) ?? false
	}
	if (isPlatformUser(user) && user.tenant_list) {
		const tenant = user.tenant_list.find(t => t.org_id === organizationId)
		if (tenant?.organization) {
			return tenant.organization.owners?.includes(email) ?? false
		}
	}
	return false
}

/** Resolve org root tenant id from /me payload without calling owner-only listProjects. */
export function resolveRootTenantIdFromUser(
	user: ConsoleUser | PlatformUser | null | undefined,
	organizationId: string,
): string | null {
	if (!user || !organizationId) {
		return null
	}

	if (isPlatformUser(user) && user.tenant_list?.length) {
		const tenants = user.tenant_list
		const rootTenant = tenants.find(t => t.org_id === organizationId && isRootProject(t.id, tenants))
		if (rootTenant) {
			return rootTenant.id
		}
	}

	return null
}

/**
 * Non-reactive ownership check for use in non-React contexts (route guards).
 * Reads store state synchronously — will not trigger re-renders.
 * For React components, use `useIsOwner()` hook instead.
 */
export function isOwner(): boolean {
	return isUserOwner(useAuthStore.getState().user)
}

export function useIsOwner(): boolean {
	return isUserOwner(useAuthStore(state => state.user))
}

export function isAdmin(): boolean {
	return isUserAdmin(useAuthStore.getState().user)
}

export function useIsAdmin(): boolean {
	return isUserAdmin(useAuthStore(state => state.user))
}

// Combined permission check - checks if user can create a project
// Returns true if user is either an owner or an admin
export function useCanCreateProject(): boolean {
	const isOwner = useIsOwner()
	const isAdmin = useIsAdmin()
	return isOwner || isAdmin
}

// Check if user is an owner of a specific organization
export function useIsOwnerOfOrganization(organizationId: string | null): boolean {
	const user = useAuthStore(state => state.user)
	return organizationId ? isOwnerOfOrganization(user, organizationId) : false
}

// ==================== V3 Permission Utilities ====================

/**
 * Hook to get user permissions from V3 API
 * Returns the entire raw permissions object or undefined if not available
 * Only PlatformUser has role/permissions, ConsoleUser does not
 *
 */
export function usePermissions() {
	const user = useAuthStore(state => state.user)

	if (!user || !isPlatformUser(user)) {
		return undefined
	}

	return user.role?.permissions
}

/**
 * Execute Command CTA / empty-state: allowed for org owners (who access via overriding
 * permissions, not granular grants) or when any loaded action type has an execute grant.
 */
export function useHasAnyExecutableActionType(): boolean {
	const isOwner = useIsOwner()
	const permissions = usePermissions()
	const allActionTypes = useActionTypesStore(state => state.allActionTypes)
	return useMemo(
		() =>
			isOwner ||
			hasAnyExecutableActionType(
				permissions,
				allActionTypes.map(t => t.type),
			),
		[isOwner, permissions, allActionTypes],
	)
}

/**
 * Owner check scoped to the organization of the current tenant. Unlike `useIsOwner` (owner of
 * ANY org in /me), this only bypasses when the user owns the org they are currently working in.
 */
export function useIsOwnerOfCurrentOrg(): boolean {
	const user = useAuthStore(state => state.user)
	const currentTenant = useAuthStore(state => state.currentTenant)
	return useMemo(() => {
		if (!user) {
			return false
		}
		// Console users have no tenant_list to resolve the org from; keep the legacy any-org check.
		if (!isPlatformUser(user)) {
			return isUserOwner(user)
		}
		if (!currentTenant) {
			return false
		}
		const orgId = user.tenant_list?.find(t => t.id === currentTenant)?.org_id
		return orgId ? isOwnerOfOrganization(user, orgId) : false
	}, [user, currentTenant])
}

/**
 * Remote Shell access: launching a shell triggers the `launch_shell` action type, so the
 * tab is gated on an execute grant for it. Owners of the CURRENT org bypass via overriding
 * permissions; owning some other org must not leak access here.
 */
export function useCanLaunchRemoteShell(): boolean {
	const isOwnerOfCurrentOrg = useIsOwnerOfCurrentOrg()
	const permissions = usePermissions()
	return useMemo(
		() => isOwnerOfCurrentOrg || hasAnyExecutableActionType(permissions, ['launch_shell']),
		[isOwnerOfCurrentOrg, permissions],
	)
}
